// Detect page reloads
console.log("Product-slider.js loaded at", new Date().toLocaleTimeString());

window.addEventListener("beforeunload", () => {
  console.log("beforeunload event - page is reloading/navigating");
  console.trace("Reload triggered from:");
});

if (!customElements.get("product-slider")) {
  customElements.define(
    "product-slider",
    class ProductSLider extends HTMLElement {
      constructor() {
        super();

        // this.updateActivePage(); // Added to show page buttons if manual results are less than a page long (before Klevu results load)

        this.cardSpacing = 20;

        this.buttonContainer = this.querySelector("[data-button-container]");
        this.navButtons = this.buttonContainer.querySelectorAll("button");
        this.currentPageElement = this.querySelector(
          ".slider-counter--current",
        );
        this.pageTotalElement = this.querySelector(".slider-counter--total");

        this.carousel = this.querySelector("[data-product-carousel]");

        this.isUpdating = false;
        this.updateTimeout = null;
        this.observerFireCount = 0;

        this.observer = new MutationObserver(() => {
          this.observerFireCount++;
          console.log(`Observer fire #${this.observerFireCount} - isUpdating: ${this.isUpdating}`);

          // Guard: prevent excessive observer fires (max 50 in quick succession)
          if (this.observerFireCount > 50) {
            console.error("Observer firing excessively - disconnecting to prevent crash!");
            this.observer.disconnect();
            return;
          }

          // Guard: ensure carousel still exists
          if (!this.carousel) return;

          if (!this.isUpdating) {
            this.isUpdating = true;
            // Clear any pending update
            clearTimeout(this.updateTimeout);

            this.updateTimeout = setTimeout(() => {
              try {
                console.log(" Timeout executing onProductsChanged");
                this.onProductsChanged();
              } catch (e) {
                console.error("Error in onProductsChanged:", e);
              }
              this.isUpdating = false;
            }, 100);
          }
        });

        // DISABLED: Observer can cause crashes on mobile with multiple sliders
        // Products load statically, so real-time observation isn't critical
        // if (this.carousel) {
        //   this.observer.observe(this.carousel, {
        //     childList: true,
        //     subtree: true,
        //   });
        // }

        let style = getComputedStyle(this.carousel);
        let spacingProperty = style.getPropertyValue("--spacing");
        this.cardSpacing = parseInt(spacingProperty);

        this.scrollTicking = false;
        this.index = 0;
        // this.maxIndex = this.productCardCount - 1;

        // Store bound functions for proper cleanup later
        this.boundHandleScroll = this.handleScroll.bind(this);
        this.boundHandleResize = this.handleWindowResize.bind(this);
        this.boundHandleButtonClick = (e) => {
          e.preventDefault();
          let moveBy = parseInt(e.currentTarget.dataset.move);
          this.moveCarousel(moveBy);
        };

        this.carousel.addEventListener("scroll", this.boundHandleScroll);

        this.navButtons.forEach((navButton) => {
          navButton.addEventListener("click", this.boundHandleButtonClick);
        });

        this.isResizing = false;

        window.addEventListener("resize", this.boundHandleResize);

        // Lazy-rendered sections dispatch this once their cards actually exist
        // in the DOM, which can happen well after the connectedCallback poller
        // below gives up. Without this, sliders whose cards load late are left
        // with no pagination/arrows until the user manually scrolls them.
        this.boundHandleProductsRefresh = () => this.onProductsChanged();
        document.addEventListener("product-badges:refresh", this.boundHandleProductsRefresh);
      }
      get maxIndex() {
        return this._maxIndex;
      }
      set maxIndex(maxIndex) {
        this._maxIndex = maxIndex;
      }
      get productCardCount() {
        // console.log("THIS: ", this.productCards);
        return this.productCards.length;
      }
      get productCards() {
        return this.carousel.querySelectorAll("product-card");
      }
      moveCarousel(moveBy) {
        let cardsPerPage = this.getCardsPerPage();
        let totalPages = Math.ceil(this.productCardCount / cardsPerPage);

        // Determine current page — if at maxIndex, we're on the last page
        let currentPage;
        if (this.index >= this.maxIndex) {
          currentPage = totalPages - 1;
        } else {
          currentPage = Math.round(this.index / cardsPerPage);
        }

        let targetPage = Math.min(
          Math.max(currentPage + moveBy, 0),
          totalPages - 1,
        );

        // Last page snaps to maxIndex so the final cards are visible
        if (targetPage === totalPages - 1) {
          this.index = this.maxIndex;
        } else {
          this.index = targetPage * cardsPerPage;
        }

        this.carousel.scrollLeft = this.index * this.cardWidth;
      }
      getCardsPerPage() {
        let style = getComputedStyle(this.carousel);
        let cardsPerPageProperty = style.getPropertyValue("--cards-per-page");
        let cardsPerPage = parseInt(cardsPerPageProperty);
        return cardsPerPage || 1;
      }
      handleScroll(e) {
        if (!this.scrollTicking) {
          window.requestAnimationFrame(() => {
            this.updateActivePage();
            this.scrollTicking = false;
          });
          this.scrollTicking = true;
        }
      }
      updateActivePage() {
        try {
          this.calculateCardWidth();
          this.calculateMaxIndex();

          if (!this.carousel || !this.cardWidth) {
            // No cards yet — hide counter and disable both buttons
            this.currentPageElement?.closest(".slider-counter")?.classList.add("hidden");
            this.navButtons.forEach((button) => button.setAttribute("disabled", true));
            return;
          }

          this.index = Math.round(this.carousel.scrollLeft / this.cardWidth);

          // Cache product count and cards per page to avoid multiple getter calls
          const productCount = this.productCardCount;
          const cardsPerPage = this.getCardsPerPage();

          // Update button states
          this.navButtons.forEach((button) => button.removeAttribute("disabled"));

          if (this.index < 1) {
            this.querySelector('button[data-move="-1"')?.setAttribute(
              "disabled",
              true,
            );
          } else if (this.index == this.maxIndex) {
            this.querySelector('button[data-move="1"')?.setAttribute(
              "disabled",
              true,
            );
          }

          // Update visibility of buttons
          const shouldHideButtons = cardsPerPage >= productCount;
          this.navButtons.forEach((button) => {
            if (shouldHideButtons) {
              button.classList.add("hidden");
            } else {
              button.classList.remove("hidden");
            }
          });

          // Update counter - show page numbers instead of card numbers
          if (this.currentPageElement && this.pageTotalElement) {
            let totalPages = Math.ceil(productCount / cardsPerPage);
            let currentPage;
            if (this.index >= this.maxIndex) {
              currentPage = totalPages;
            } else {
              currentPage = Math.floor(this.index / cardsPerPage) + 1;
            }
            this.currentPageElement.textContent = currentPage;
            this.pageTotalElement.textContent = totalPages;

            const counterContainer =
              this.currentPageElement.closest(".slider-counter");

            if (totalPages <= 1) {
              counterContainer?.classList.add("hidden");
            } else {
              counterContainer?.classList.remove("hidden");
            }
          }
        } catch (e) {
          console.error("Error updating active page:", e);
        }
      }
      calculateCardWidth() {
        try {
          const firstCard = this.productCards[0];
          if (!firstCard || !this.carousel) return;
          this.cardWidth = firstCard.getBoundingClientRect().width + this.cardSpacing;
        } catch (e) {
          console.error("Error calculating card width:", e);
        }
      }
      calculateMaxIndex() {
        try {
          // Guard against invalid cardWidth or carousel
          if (!this.cardWidth || this.cardWidth <= 0 || !this.carousel) {
            this.maxIndex = Math.max(0, this.productCardCount - 1);
            return;
          }

          let perScreen = Math.max(
            1,
            Math.ceil(
              this.carousel.getBoundingClientRect().width / this.cardWidth,
            ),
          );

          if (perScreen <= 1) {
            this.maxIndex = this.productCardCount - 1;
          } else {
            this.maxIndex = this.productCardCount - perScreen;
          }
        } catch (e) {
          console.error("Error calculating max index:", e);
        }
      }
      handleWindowResize() {
        if (!this.isResizing) {
          this.isResizing = true;
          this.calculateCardWidth();
          this.calculateMaxIndex();
          this.updateActivePage();
          // Keep flag active until next frame
          requestAnimationFrame(() => {
            this.isResizing = false;
          });
        }
      }
      onProductsChanged() {
        if (this.productCards.length === 0) return;

        this.calculateCardWidth();
        this.calculateMaxIndex();
        this.updateActivePage();
      }
      init() {
        console.log("Slider init() called");
        this.calculateCardWidth();
        this.calculateMaxIndex();
        this.updateActivePage();
        console.log("Slider init() complete");
      }
      connectedCallback() {
        console.log("Product-slider connectedCallback - cards:", this.productCards.length);
        this.init();

        let pollAttempts = 0;
        this._cardPoller = setInterval(() => {
          pollAttempts++;
          const firstCard = this.productCards[0];
          const hasWidth = firstCard && firstCard.getBoundingClientRect().width > 0;
          if (hasWidth) {
            clearInterval(this._cardPoller);
            this._cardPoller = null;
            this.init();
          } else if (pollAttempts >= 50) {
            clearInterval(this._cardPoller);
            this._cardPoller = null;
          }
        }, 100);
      }
      disconnectedCallback() {
        try {
          // Clean up observer
          if (this.observer) {
            this.observer.disconnect();
          }

          // Clean up card poller
          if (this._cardPoller) {
            clearInterval(this._cardPoller);
          }

          // Clean up timeouts
          clearTimeout(this.updateTimeout);

          // Clean up event listeners
          if (this.carousel) {
            this.carousel.removeEventListener("scroll", this.boundHandleScroll);
          }
          if (this.navButtons) {
            this.navButtons.forEach((navButton) => {
              navButton.removeEventListener("click", this.boundHandleButtonClick);
            });
          }
          window.removeEventListener("resize", this.boundHandleResize);
          document.removeEventListener("product-badges:refresh", this.boundHandleProductsRefresh);
        } catch (e) {
          console.error("Error in disconnectedCallback:", e);
        }
      }
    },
  );
}
